<script setup lang="ts">
import {computed, onMounted, provide, reactive, watch, watchEffect} from 'vue'
import type {DataRow} from '@hantera/design-system'
import {InspectorLayout, type InspectorProvider} from '@hantera/design-system'
import {useContext, useDialogs, useLocalization, useProcesses} from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import {useClientStore} from '@/stores/iam/client-store'
import ClientSidebar from './ClientSidebar.vue'
import ClientServiceAccountTab from './ClientServiceAccountTab.vue'
import ClientUserAuthTab from './ClientUserAuthTab.vue'
import ClientSecretsTab from './ClientSecretsTab.vue'
import ClientSessionsTab from './ClientSessionsTab.vue'

const t = useLocalization('iam.clients')

const props = defineProps<{
  row: DataRow
}>()

const clientId = computed(() => props.row.key)

const context = useContext()
const dialogs = useDialogs()
const processes = useProcesses()
const iamService = useBackend().iam

const store = useClientStore(clientId.value, {
  context,
  dialogs,
  processes,
  iamService,
})

provide('clientStore', store)

const revokableSessions = computed(() => {
  return store.sessions.filter((s) => !s.isCurrent && !s.isRevoked)
})

const inspectorProvider: InspectorProvider = reactive({
  state: { label: t('status.loading') },
  title: '',
  labels: [],
  summary: ClientSidebar,
  summaryLabel: t('tabs.overview'),
  selectedTab: 'service-account',
  tabs: [],
})

watchEffect(() => {
  const c = store.client

  if (!c) return

  inspectorProvider.state = { label: t('status.active'), hue: 133 }
  inspectorProvider.title = c.name || c.id

  const roleCount = c.roles.length
  inspectorProvider.labels = [
    c.id,
  ]
})

watchEffect(() => {
  const c = store.client

  if (!c) {
    inspectorProvider.tabs = []
    return
  }

  const roleCount = c.roles.length
  const sessionsCount = store.sessions.length
  const secretCount = store.secretCount()

  inspectorProvider.tabs = [
    {
      id: 'service-account',
      label: t('tabs.serviceAccount'),
      component: ClientServiceAccountTab,
    },
    {
      id: 'user-auth',
      label: t('tabs.userAuth'),
      component: ClientUserAuthTab,
    },
    {
      id: 'secrets',
      label: t('tabs.secrets'),
      tags: secretCount > 0 ? [{ style: 'primary', value: secretCount.toString() }] : [],
      component: ClientSecretsTab,
    },
    {
      id: 'sessions',
      label: t('tabs.sessions'),
      tags: sessionsCount > 0 ? [{ style: 'primary', value: sessionsCount.toString() }] : [],
      component: ClientSessionsTab,
      toolbar: [{
        type: 'button',
        align: 'right',
        buttonStyle: 'danger',
        label: t('actions.revokeCount', { count: revokableSessions.value.length }),
        id: 'revokeAll',
        disabled: revokableSessions.value.length === 0,
        async onClick() {
          if (revokableSessions.value.length === 0) return

          const count = revokableSessions.value.length
          const confirmed = await store.dialogs.confirm(
            t('dialogs.revokeSessionsPrompt', { count }),
            t('dialogs.revokeSessionsTitle')
          )

          if (!confirmed) return

          try {
            const clientId = store.client?.id
            if (!clientId) return

            await Promise.all(
              revokableSessions.value.map((session) => store.iamService.revokeClientSession(clientId, session.id))
            )
            await store.refresh()
            store.processes.createProcess({
              description: t('actions.revokeAll'),
            }).complete()
          } catch (error: any) {
            console.error('Failed to revoke sessions:', error)
            store.processes.error({
              message: error.response?.data?.message || 'Failed to revoke sessions',
            })
          }
        }
      }]
    },
  ]
})

onMounted(() => {
  store.refresh()
})

watch(clientId, (newId) => {
  if (newId) {
    store.refresh()
  }
})
</script>

<template>
  <InspectorLayout v-if="inspectorProvider" :provider="inspectorProvider" />
</template>

<style scoped></style>
